import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import socketService from '../../services/socket';
import './Admin.css';
import './AdminRequests.css';

function AdminRequests() {
    const { hasPermission } = useAuth();
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [adminUnreadCount, setAdminUnreadCount] = useState(0);
    const [search, setSearch] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [printRequest, setPrintRequest] = useState(null);
    const [history, setHistory] = useState([]);
    const [approveData, setApproveData] = useState({
        allow_self_print: false,
        pickup_location: '',
        admin_note: ''
    });
    const [rejectReason, setRejectReason] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [processing, setProcessing] = useState(false);
    const [defaultPickupLocation, setDefaultPickupLocation] = useState('ฝ่ายการเงิน ชั้น 2 อาคารอำนวยการ');
    // ส่ง 50 ทวิ (อัพโหลดไฟล์จากระบบการเงิน) เข้าอีเมลผู้ร้องขอ
    const [showSendModal, setShowSendModal] = useState(false);
    const [sendRequest, setSendRequest] = useState(null);
    const [sendFiles, setSendFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [sentList, setSentList] = useState([]);
    const [sentCount, setSentCount] = useState(0);
    const [showSentListModal, setShowSentListModal] = useState(false);

    const fetchStats = useCallback(async () => {
        try {
            const response = await api.get('/request/admin/stats');
            setStats(response.data);
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    }, []);

    const fetchDefaultPickupLocation = useCallback(async () => {
        try {
            const response = await api.get('/admin/report-settings');
            if (response.data.request?.pickup_location?.value) {
                setDefaultPickupLocation(response.data.request.pickup_location.value);
            }
        } catch (err) {
            console.error('Error fetching default pickup location:', err);
        }
    }, []);

    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (filter !== 'all') params.append('status', filter);
            if (search) params.append('search', search);

            const response = await api.get(`/request/admin/all?${params.toString()}`);
            setRequests(response.data);
        } catch (err) {
            console.error('Error fetching requests:', err);
        } finally {
            setLoading(false);
        }
    }, [filter, search]);

    const fetchSentList = useCallback(async () => {
        try {
            const res = await api.get('/request/admin/sent-50tawi');
            setSentList(res.data.items || []);
            setSentCount(res.data.count || 0);
        } catch (err) {
            // ไม่ critical — เงียบไว้
        }
    }, []);

    const fetchAdminUnreadCount = useCallback(async () => {
        if (hasPermission('chat.admin')) {
            try {
                const response = await api.get('/chat/admin/unread-count');
                setAdminUnreadCount(response.data.total_unread || response.data.count || 0);
            } catch (err) {
                // Ignore error
            }
        }
    }, [hasPermission]);

    useEffect(() => {
        fetchStats();
        fetchRequests();
        fetchAdminUnreadCount();
        fetchDefaultPickupLocation();
        fetchSentList();
    }, [fetchStats, fetchRequests, fetchAdminUnreadCount, fetchDefaultPickupLocation, fetchSentList]);

    // Listen for realtime updates via Socket.IO
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            socketService.connect(token);

            // Listen for new requests from users
            const handleNewRequest = (data) => {
                // Refresh stats and requests list
                fetchStats();
                fetchRequests();

                // Show notification
                setMessage({
                    type: 'info',
                    text: `มีคำร้องใหม่จาก ${data.user_name}: ${data.request_type}`
                });

                // Auto-hide after 5 seconds
                setTimeout(() => {
                    setMessage({ type: '', text: '' });
                }, 5000);
            };

            socketService.on('new_request', handleNewRequest);

            return () => {
                socketService.off('new_request', handleNewRequest);
            };
        }
    }, [fetchStats, fetchRequests]);

    const openApproveModal = (request) => {
        setSelectedRequest(request);
        setApproveData({
            allow_self_print: false,
            pickup_location: defaultPickupLocation,
            admin_note: ''
        });
        setShowApproveModal(true);
    };

    const openRejectModal = (request) => {
        setSelectedRequest(request);
        setRejectReason('');
        setShowRejectModal(true);
    };

    const openHistoryModal = async (request) => {
        try {
            const response = await api.get(`/request/admin/history/${request.id}`);
            setHistory(response.data);
            setSelectedRequest(request);
            setShowHistoryModal(true);
        } catch (err) {
            console.error('Error fetching history:', err);
        }
    };

    const openUserModal = (request) => {
        setSelectedUser({
            id: request.user_id,
            name: request.user_name,
            cid: request.user_cid,
            department: request.user_department || '-',
            position: request.user_position || '-',
            phone: request.user_phone || '-',
            email: request.user_email || '-'
        });
        setShowUserModal(true);
    };

    const openPrintModal = (request) => {
        setPrintRequest(request);
        setShowPrintModal(true);
    };

    const handlePrintSlip = () => {
        if (!printRequest) return;
        // Navigate to new admin print slip page
        const url = `/admin/print-slip?cid=${printRequest.user_cid}&name=${encodeURIComponent(printRequest.user_name)}&requestId=${printRequest.id}`;
        setShowPrintModal(false);
        navigate(url);
    };

    const handlePrint50Tawi = () => {
        if (!printRequest) return;
        // Navigate to new admin print 50 tawi page
        const url = `/admin/print-50tawi?cid=${printRequest.user_cid}&name=${encodeURIComponent(printRequest.user_name)}&requestId=${printRequest.id}`;
        setShowPrintModal(false);
        navigate(url);
    };

    const handleApprove = async () => {
        if (!selectedRequest) return;
        setProcessing(true);

        try {
            // หนังสือรับรองเงินเดือน = ยืนยันนัดหมาย: ไม่มี self-print, รับเอกสารที่ห้องการเงินเสมอ
            const payload = selectedRequest.request_type === 'salary_cert'
                ? { ...approveData, allow_self_print: false, pickup_location: approveData.pickup_location || 'ห้องการเงิน' }
                : approveData;
            const res = await api.post(`/request/admin/approve/${selectedRequest.id}`, payload);
            setMessage({ type: 'success', text: res.data?.message || 'อนุมัติคำร้องสำเร็จ' });
            setShowApproveModal(false);
            fetchRequests();
            fetchStats();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!selectedRequest || !rejectReason.trim()) {
            setMessage({ type: 'error', text: 'กรุณาระบุเหตุผล' });
            return;
        }
        setProcessing(true);

        try {
            await api.post(`/request/admin/reject/${selectedRequest.id}`, {
                rejected_reason: rejectReason
            });
            setMessage({ type: 'success', text: 'ปฏิเสธคำร้องสำเร็จ' });
            setShowRejectModal(false);
            fetchRequests();
            fetchStats();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setProcessing(false);
        }
    };

    const handleAcknowledge = async (request) => {
        if (!window.confirm('ยืนยันการรับดำเนินการคำร้องนี้?')) return;

        try {
            await api.post(`/request/admin/acknowledge/${request.id}`);
            setMessage({ type: 'success', text: 'รับดำเนินการสำเร็จ' });
            fetchRequests();
            fetchStats();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    const handleComplete = async (request) => {
        const confirmText = request.request_type === 'salary_cert'
            ? 'ยืนยันว่า "หนังสือออกแล้ว"?\n\nระบบจะส่งอีเมลแจ้งผู้ขอให้มารับเอกสารที่ห้องการเงิน'
            : 'ยืนยันว่าผู้ใช้รับเอกสารแล้ว?';
        if (!window.confirm(confirmText)) return;

        try {
            const res = await api.post(`/request/admin/complete/${request.id}`);
            setMessage({ type: 'success', text: res.data?.message || 'บันทึกการรับเอกสารสำเร็จ' });
            fetchRequests();
            fetchStats();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    const openSendModal = (request) => {
        setSendRequest(request);
        setSendFiles([]);
        setMessage({ type: '', text: '' });
        setShowSendModal(true);
    };

    const handleSend50Tawi = async () => {
        if (!sendRequest) return;
        if (!sendFiles.length) {
            setMessage({ type: 'error', text: 'กรุณาเลือกไฟล์ PDF 50 ทวิ อย่างน้อย 1 ไฟล์' });
            return;
        }
        setSending(true);
        setMessage({ type: '', text: '' });
        try {
            const formData = new FormData();
            sendFiles.forEach(f => formData.append('documents', f));
            const res = await api.post(`/request/admin/send-50tawi/${sendRequest.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMessage({ type: 'success', text: res.data.message || 'ส่ง 50 ทวิ ทางอีเมลแล้ว' });
            setShowSendModal(false);
            setSendFiles([]);
            fetchRequests();
            fetchStats();
            fetchSentList();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || err.response?.data?.error || 'ส่งไม่สำเร็จ กรุณาลองใหม่' });
        } finally {
            setSending(false);
        }
    };

    const getRequestTypeName = (type) => {
        const typeNames = {
            'salary_slip': 'สลิปเงินเดือน',
            'tax_cert': '50 ทวิ',
            'salary_cert': 'หนังสือรับรองเงินเดือน'
        };
        return typeNames[type] || type;
    };

    // เปิดดูรูปยืนยันคำขอหนังสือรับรองเงินเดือน (บัตร ปชช. บนกระดาษเขียนข้อความ) — โหลด blob ผ่าน token
    const viewCertEvidence = async (requestId) => {
        try {
            const res = await api.get(`/request/admin/${requestId}/evidence`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            alert(e.response?.data?.error || 'โหลดรูปหลักฐานไม่สำเร็จ');
        }
    };

    const getMonthName = (month) => {
        const months = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
            'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
        return months[month] || '';
    };

    // ข้อความนัดหมายเขียนคำร้อง (หนังสือรับรองเงินเดือน)
    const formatAppointment = (request) => {
        if (!request?.appointment_date) return null;
        const d = new Date(request.appointment_date);
        if (isNaN(d.getTime())) return null;
        const dateText = d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
        return `${dateText} ${request.appointment_period === 'afternoon' ? 'บ่าย (13:00–16:00)' : 'เช้า (08:30–12:00)'}`;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            pending: { label: 'ส่งคำขอแล้ว', class: 'badge-warning' },
            processing: { label: 'กำลังเตรียมเอกสาร', class: 'badge-primary' },
            approved: { label: 'อนุมัติแล้ว', class: 'badge-success' },
            rejected: { label: 'ปฏิเสธ', class: 'badge-error' },
            completed: { label: 'เสร็จสิ้น', class: 'badge-info' },
            cancelled: { label: 'ยกเลิก', class: 'badge-muted' }
        };
        const info = statusMap[status] || { label: status, class: '' };
        return <span className={`badge ${info.class}`}>{info.label}</span>;
    };

    // คำนวณเวลาที่ผ่านไปตั้งแต่ส่งคำร้อง
    const getTimeElapsed = (createdAt) => {
        const now = new Date();
        const created = new Date(createdAt);
        const diffMs = now - created;

        const minutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (days > 0) {
            const remainingHours = hours % 24;
            if (remainingHours > 0) {
                return { text: `${days} วัน ${remainingHours} ชม.`, isLong: days >= 3 };
            }
            return { text: `${days} วัน`, isLong: days >= 3 };
        }
        if (hours > 0) {
            const remainingMinutes = minutes % 60;
            if (remainingMinutes > 0) {
                return { text: `${hours} ชม. ${remainingMinutes} นาที`, isLong: hours >= 24 };
            }
            return { text: `${hours} ชม.`, isLong: hours >= 24 };
        }
        if (minutes > 0) {
            return { text: `${minutes} นาที`, isLong: false };
        }
        return { text: 'เมื่อสักครู่', isLong: false };
    };

    return (
        <div className="admin-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>คำร้องขอเอกสาร</span>
            </div>

            <div className="admin-header">
                <h1>คำร้องขอเอกสาร</h1>
                <p>จัดการคำร้องขอสลิปเงินเดือนและ 50 ทวิ</p>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* Stats Cards */}
            {stats && (
                <div className="stats-grid mb-3">
                    <div className={`stat-card warning ${stats.pending_count > 0 ? 'has-badge' : ''}`}>
                        {stats.pending_count > 0 && <span className="stat-badge">ใหม่</span>}
                        <div className="stat-icon orange">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="12" y1="18" x2="12" y2="12"/>
                                <line x1="9" y1="15" x2="15" y2="15"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.pending_count}</div>
                            <div className="stat-label">ส่งคำขอแล้ว</div>
                        </div>
                    </div>
                    <div className="stat-card info">
                        <div className="stat-icon purple">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                <line x1="16" y1="2" x2="16" y2="6"/>
                                <line x1="8" y1="2" x2="8" y2="6"/>
                                <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.today_requests}</div>
                            <div className="stat-label">คำร้องวันนี้</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Access Menu */}
            <div className="quick-menu-section">
                <h3 className="section-title">เมนูจัดการ</h3>
                <div className="quick-menu-grid">
                    {hasPermission('chat.admin') && (
                        <Link to="/admin/chat" className="quick-menu-card">
                            <div className="quick-menu-icon blue">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                {adminUnreadCount > 0 && (
                                    <span className="menu-badge">{adminUnreadCount > 99 ? '99+' : adminUnreadCount}</span>
                                )}
                            </div>
                            <div className="quick-menu-content">
                                <h4>ระบบแชท</h4>
                                <p>สนทนากับผู้ใช้งานระบบ</p>
                            </div>
                        </Link>
                    )}
                    <div className="quick-menu-card active">
                        <div className="quick-menu-icon orange">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                            </svg>
                        </div>
                        <div className="quick-menu-content">
                            <h4>คำร้องขอเอกสาร</h4>
                            <p>อนุมัติ/ปฏิเสธคำร้องขอสลิปและ 50 ทวิ</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="steam-card mb-3">
                <div className="steam-card-body">
                    <div className="filters-row">
                        <div className="filter-tabs">
                            {['pending', 'processing', 'approved', 'completed', 'rejected', 'all'].map(status => (
                                <button
                                    key={status}
                                    className={`filter-tab ${filter === status ? 'active' : ''}`}
                                    onClick={() => setFilter(status)}
                                >
                                    {status === 'pending' && 'ส่งคำขอแล้ว'}
                                    {status === 'processing' && 'กำลังเตรียมเอกสาร'}
                                    {status === 'approved' && 'อนุมัติแล้ว'}
                                    {status === 'completed' && 'เสร็จสิ้น'}
                                    {status === 'rejected' && 'ปฏิเสธ'}
                                    {status === 'all' && 'ทั้งหมด'}
                                </button>
                            ))}
                        </div>
                        <div className="search-box">
                            <input
                                type="text"
                                className="steam-input"
                                placeholder="ค้นหาชื่อหรือรหัส..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* สรุปจำนวน 50 ทวิ ที่ส่งทางอีเมลแล้ว — คลิกเพื่อดูรายการ */}
            {sentCount > 0 && (
                <div className="steam-card mb-3">
                    <div className="steam-card-body">
                        <button
                            type="button"
                            className="sent-summary-banner"
                            onClick={() => setShowSentListModal(true)}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                            </svg>
                            <span>ส่ง 50 ทวิ ทางอีเมลแล้วทั้งหมด <strong>{sentCount}</strong> ราย</span>
                            <span className="sent-summary-link">ดูรายการ →</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Request Table */}
            <div className="steam-card">
                <div className="steam-card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="flex-center" style={{ padding: '60px 0' }}>
                            <div className="spinner"></div>
                        </div>
                    ) : requests.length === 0 ? (
                        <div className="empty-state">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z"/>
                            </svg>
                            <p>ไม่มีคำร้อง</p>
                        </div>
                    ) : (
                        <>
                        {/* Desktop Table View */}
                        <table className="data-table desktop-only">
                            <thead>
                                <tr>
                                    <th>ผู้ขอ</th>
                                    <th>ประเภท</th>
                                    <th>งวด</th>
                                    <th>สถานะ</th>
                                    <th>วันที่ขอ</th>
                                    <th>รอ</th>
                                    <th>การจัดการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map(request => (
                                    <tr key={request.id}>
                                        <td>
                                            <div
                                                className="user-cell user-cell-clickable"
                                                onClick={() => openUserModal(request)}
                                                title="คลิกเพื่อดูข้อมูลผู้ใช้"
                                            >
                                                <strong>{request.user_name}</strong>
                                                <small>{request.user_cid}</small>
                                                {request.contact_phone && (
                                                    <small className="contact-phone">
                                                        <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                                                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                                                        </svg>
                                                        {request.contact_phone}
                                                    </small>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {getRequestTypeName(request.request_type)}
                                            {request.request_reason && (
                                                <div className="request-reason-tag" title={request.request_reason}>
                                                    {request.request_reason}
                                                </div>
                                            )}
                                            {request.evidence_image && (
                                                <button
                                                    type="button"
                                                    className="steam-btn steam-btn-secondary"
                                                    style={{ padding: '2px 8px', fontSize: 12, marginTop: 4 }}
                                                    onClick={() => viewCertEvidence(request.id)}
                                                    title="รูปบัตร ปชช. บนกระดาษเขียนข้อความขอเอกสาร"
                                                >
                                                    🪪 ดูรูปยืนยันคำขอ
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            {/* หนังสือรับรองเงินเดือนไม่ผูกกับปี — แสดงวันนัดเขียนคำร้องแทน */}
                                            {request.request_type === 'salary_cert'
                                                ? (formatAppointment(request) ? `📅 ${formatAppointment(request)}` : '—')
                                                : request.request_type === 'salary_slip'
                                                    ? `${getMonthName(request.document_month)} ${request.document_year + 543}`
                                                    : `ปี ${request.document_year + 543}`
                                            }
                                        </td>
                                        <td>
                                            {getStatusBadge(request.status)}
                                            {request.emailed_at && (
                                                <div className="emailed-tag" title={`ส่ง 50 ทวิ ทางอีเมลแล้ว ${new Date(request.emailed_at).toLocaleString('th-TH')}`}>
                                                    📧 ส่งอีเมลแล้ว
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {new Date(request.created_at).toLocaleDateString('th-TH', {
                                                day: 'numeric',
                                                month: 'short',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </td>
                                        <td>
                                            {request.status === 'pending' ? (
                                                (() => {
                                                    const elapsed = getTimeElapsed(request.created_at);
                                                    return (
                                                        <span className={`time-elapsed-badge ${elapsed.isLong ? 'long' : ''}`}>
                                                            <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                                                                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                                            </svg>
                                                            {elapsed.text}
                                                        </span>
                                                    );
                                                })()
                                            ) : (
                                                <span className="time-elapsed-badge completed">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                {request.status === 'pending' && (
                                                    <>
                                                        <button
                                                            className="btn-action acknowledge"
                                                            onClick={() => handleAcknowledge(request)}
                                                            title="รับดำเนินการ"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            className="btn-action approve"
                                                            onClick={() => openApproveModal(request)}
                                                            title="อนุมัติ"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            className="btn-action reject"
                                                            onClick={() => openRejectModal(request)}
                                                            title="ปฏิเสธ"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}
                                                {request.status === 'processing' && (
                                                    <>
                                                        <button
                                                            className="btn-action approve"
                                                            onClick={() => openApproveModal(request)}
                                                            title="อนุมัติ"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            className="btn-action reject"
                                                            onClick={() => openRejectModal(request)}
                                                            title="ปฏิเสธ"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}
                                                {request.status === 'approved' && (
                                                    <>
                                                        <button
                                                            className="btn-action print"
                                                            onClick={() => openPrintModal(request)}
                                                            title="พิมพ์เอกสาร"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                                <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                                                            </svg>
                                                        </button>
                                                        {!request.allow_self_print && (
                                                            <button
                                                                className="btn-action complete"
                                                                onClick={() => handleComplete(request)}
                                                                title={request.request_type === 'salary_cert'
                                                                    ? 'หนังสือออกแล้ว — แจ้งผู้ขอมารับทางอีเมล'
                                                                    : 'รับเอกสารแล้ว'}
                                                            >
                                                                <svg viewBox="0 0 24 24" fill="currentColor">
                                                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                                {request.request_type === 'tax_cert' && request.status !== 'rejected' && request.status !== 'cancelled' && (
                                                    <button
                                                        className="btn-action send-email"
                                                        onClick={() => openSendModal(request)}
                                                        title={request.emailed_at ? 'ส่ง 50 ทวิ ทางอีเมลอีกครั้ง' : 'อัพโหลด & ส่ง 50 ทวิ ทางอีเมล'}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                                        </svg>
                                                    </button>
                                                )}
                                                <button
                                                    className="btn-action history"
                                                    onClick={() => openHistoryModal(request)}
                                                    title="ประวัติ"
                                                >
                                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Mobile Card View */}
                        <div className="request-cards mobile-only">
                            {requests.map(request => {
                                const elapsed = getTimeElapsed(request.created_at);
                                return (
                                    <div key={request.id} className="request-card">
                                        <div className="request-card-header">
                                            <div
                                                className="request-card-user"
                                                onClick={() => openUserModal(request)}
                                            >
                                                <div className="request-card-avatar">
                                                    {request.user_name?.charAt(0) || 'U'}
                                                </div>
                                                <div className="request-card-info">
                                                    <strong>{request.user_name}</strong>
                                                    <small>{request.user_cid}</small>
                                                </div>
                                            </div>
                                            <div className="request-card-status">
                                                {getStatusBadge(request.status)}
                                            </div>
                                        </div>

                                        <div className="request-card-body">
                                            <div className="request-card-row">
                                                <span className="request-card-label">ประเภท</span>
                                                <span className="request-card-value">{getRequestTypeName(request.request_type)}</span>
                                            </div>
                                            <div className="request-card-row">
                                                <span className="request-card-label">งวด</span>
                                                <span className="request-card-value">
                                                    {request.request_type === 'salary_cert'
                                                        ? (formatAppointment(request) ? `📅 นัดเขียนคำร้อง ${formatAppointment(request)}` : '—')
                                                        : request.request_type === 'salary_slip'
                                                            ? `${getMonthName(request.document_month)} ${request.document_year + 543}`
                                                            : `ปี ${request.document_year + 543}`
                                                    }
                                                </span>
                                            </div>
                                            <div className="request-card-row">
                                                <span className="request-card-label">วันที่ขอ</span>
                                                <span className="request-card-value">
                                                    {new Date(request.created_at).toLocaleDateString('th-TH', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </div>
                                            {request.request_reason && (
                                                <div className="request-card-row">
                                                    <span className="request-card-label">เหตุผล</span>
                                                    <span className="request-card-value">{request.request_reason}</span>
                                                </div>
                                            )}
                                            {request.evidence_image && (
                                                <div className="request-card-row">
                                                    <span className="request-card-label">หลักฐานคำขอ</span>
                                                    <button
                                                        type="button"
                                                        className="steam-btn steam-btn-secondary"
                                                        style={{ padding: '3px 10px', fontSize: 13 }}
                                                        onClick={() => viewCertEvidence(request.id)}
                                                    >
                                                        🪪 ดูรูปยืนยันคำขอ
                                                    </button>
                                                </div>
                                            )}
                                            {request.contact_phone && (
                                                <div className="request-card-row">
                                                    <span className="request-card-label">โทรศัพท์</span>
                                                    <a href={`tel:${request.contact_phone}`} className="request-card-phone">
                                                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                                                        </svg>
                                                        {request.contact_phone}
                                                    </a>
                                                </div>
                                            )}
                                            {request.status === 'pending' && (
                                                <div className="request-card-row">
                                                    <span className="request-card-label">รอมาแล้ว</span>
                                                    <span className={`time-elapsed-badge ${elapsed.isLong ? 'long' : ''}`}>
                                                        <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                                                            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                                        </svg>
                                                        {elapsed.text}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="request-card-actions">
                                            {request.status === 'pending' && (
                                                <>
                                                    <button
                                                        className="request-card-btn acknowledge"
                                                        onClick={() => handleAcknowledge(request)}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                                        </svg>
                                                        รับดำเนินการ
                                                    </button>
                                                    <button
                                                        className="request-card-btn approve"
                                                        onClick={() => openApproveModal(request)}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                        </svg>
                                                        อนุมัติ
                                                    </button>
                                                    <button
                                                        className="request-card-btn reject"
                                                        onClick={() => openRejectModal(request)}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                                        </svg>
                                                        ปฏิเสธ
                                                    </button>
                                                </>
                                            )}
                                            {request.status === 'processing' && (
                                                <>
                                                    <button
                                                        className="request-card-btn approve"
                                                        onClick={() => openApproveModal(request)}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                        </svg>
                                                        อนุมัติ
                                                    </button>
                                                    <button
                                                        className="request-card-btn reject"
                                                        onClick={() => openRejectModal(request)}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                                        </svg>
                                                        ปฏิเสธ
                                                    </button>
                                                </>
                                            )}
                                            {request.status === 'approved' && (
                                                <>
                                                    <button
                                                        className="request-card-btn print"
                                                        onClick={() => openPrintModal(request)}
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                                                        </svg>
                                                        พิมพ์
                                                    </button>
                                                    {!request.allow_self_print && (
                                                        <button
                                                            className="request-card-btn complete"
                                                            onClick={() => handleComplete(request)}
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                            </svg>
                                                            {request.request_type === 'salary_cert' ? 'หนังสือออกแล้ว' : 'รับแล้ว'}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                            {request.request_type === 'tax_cert' && request.status !== 'rejected' && request.status !== 'cancelled' && (
                                                <button
                                                    className="request-card-btn send-email"
                                                    onClick={() => openSendModal(request)}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                                    </svg>
                                                    {request.emailed_at ? 'ส่ง 50 ทวิ อีกครั้ง' : 'อัพโหลด & ส่ง 50 ทวิ'}
                                                </button>
                                            )}
                                            <button
                                                className="request-card-btn history"
                                                onClick={() => openHistoryModal(request)}
                                            >
                                                <svg viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
                                                </svg>
                                                ประวัติ
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        </>
                    )}
                </div>
            </div>

            {/* Approve Modal - Apple Style */}
            {showApproveModal && selectedRequest && (
                <div className="apple-modal-overlay" onClick={() => setShowApproveModal(false)}>
                    <div className="apple-modal" onClick={e => e.stopPropagation()}>
                        <div className="apple-modal-header">
                            <h2>อนุมัติคำร้อง</h2>
                            <p className="apple-modal-subtitle">ตรวจสอบข้อมูลก่อนอนุมัติ</p>
                        </div>

                        <div className="apple-modal-body">
                            {/* Request Info Card */}
                            <div className="apple-info-card">
                                <div className="apple-info-row">
                                    <span className="apple-info-label">ผู้ขอ</span>
                                    <span className="apple-info-value">{selectedRequest.user_name}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">ประเภท</span>
                                    <span className="apple-info-value">{getRequestTypeName(selectedRequest.request_type)}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">งวด</span>
                                    <span className="apple-info-value">
                                        {selectedRequest.request_type === 'salary_cert'
                                            ? (formatAppointment(selectedRequest) ? `📅 นัดเขียนคำร้อง ${formatAppointment(selectedRequest)}` : '—')
                                            : selectedRequest.request_type === 'salary_slip'
                                                ? `${getMonthName(selectedRequest.document_month)} ${selectedRequest.document_year + 543}`
                                                : `ปี ${selectedRequest.document_year + 543}`
                                        }
                                    </span>
                                </div>
                                {selectedRequest.contact_phone && (
                                    <div className="apple-info-row highlight">
                                        <span className="apple-info-label">
                                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" style={{marginRight: '6px'}}>
                                                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                                            </svg>
                                            โทรติดต่อ
                                        </span>
                                        <span className="apple-info-value phone-value">{selectedRequest.contact_phone}</span>
                                    </div>
                                )}
                                <div className="apple-info-row">
                                    <span className="apple-info-label">
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" style={{marginRight: '6px'}}>
                                            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                        </svg>
                                        รอมาแล้ว
                                    </span>
                                    <span className={`apple-info-value ${getTimeElapsed(selectedRequest.created_at).isLong ? 'text-danger' : ''}`}>
                                        {getTimeElapsed(selectedRequest.created_at).text}
                                    </span>
                                </div>
                            </div>

                            {/* หนังสือรับรองเงินเดือน: การอนุมัติ = ยืนยันนัดหมายให้ผู้ขอมาเขียนคำร้อง (ไม่มี self-print) */}
                            {selectedRequest.request_type === 'salary_cert' && (
                                <div className="apple-option-card" style={{ padding: '14px 16px' }}>
                                    <strong>📅 ยืนยันนัดหมายเขียนคำร้อง</strong>
                                    <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.6 }}>
                                        ผู้ขอจะมาเขียนคำร้องที่ห้องการเงิน{' '}
                                        <b>{formatAppointment(selectedRequest) || 'ตามวันเวลาที่นัดไว้'}</b>
                                        <br />เมื่อกดยืนยัน ระบบจะส่งอีเมลแจ้งผู้ขอให้มาตามนัด (พร้อมบัตรประชาชน)
                                        <br />หลังผู้ขอเขียนคำร้องและหนังสือออกแล้ว อย่าลืมกลับมาติ๊ก{' '}
                                        <b>"หนังสือออกแล้ว"</b> เพื่อให้ระบบส่งอีเมลแจ้งผู้ขอมารับเอกสาร
                                    </div>
                                </div>
                            )}

                            {/* Options */}
                            {selectedRequest.request_type !== 'salary_cert' && (
                                <div className="apple-option-card">
                                    <label className="apple-toggle-row">
                                        <div className="apple-toggle-content">
                                            <span className="apple-toggle-title">อนุญาตให้พิมพ์เอกสารเอง</span>
                                            <span className="apple-toggle-desc">เอกสารจะมีลายเซ็นและตราประทับอิเล็กทรอนิกส์</span>
                                        </div>
                                        <div className={`apple-toggle ${approveData.allow_self_print ? 'active' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={approveData.allow_self_print}
                                                onChange={(e) => setApproveData({
                                                    ...approveData,
                                                    allow_self_print: e.target.checked
                                                })}
                                            />
                                            <span className="apple-toggle-slider"></span>
                                        </div>
                                    </label>
                                </div>
                            )}

                            {selectedRequest.request_type !== 'salary_cert' && !approveData.allow_self_print && (
                                <div className="apple-form-group">
                                    <label className="apple-label">สถานที่รับเอกสาร</label>
                                    <input
                                        type="text"
                                        className="apple-input"
                                        value={approveData.pickup_location}
                                        onChange={(e) => setApproveData({
                                            ...approveData,
                                            pickup_location: e.target.value
                                        })}
                                        placeholder="ระบุสถานที่รับเอกสาร"
                                    />
                                </div>
                            )}

                            <div className="apple-form-group">
                                <label className="apple-label">หมายเหตุ (ไม่บังคับ)</label>
                                <textarea
                                    className="apple-textarea"
                                    rows="3"
                                    value={approveData.admin_note}
                                    onChange={(e) => setApproveData({
                                        ...approveData,
                                        admin_note: e.target.value
                                    })}
                                    placeholder="เพิ่มหมายเหตุถ้าต้องการ..."
                                />
                            </div>
                        </div>

                        <div className="apple-modal-footer">
                            <button
                                className="apple-btn apple-btn-secondary"
                                onClick={() => setShowApproveModal(false)}
                            >
                                ยกเลิก
                            </button>
                            <button
                                className="apple-btn apple-btn-primary"
                                onClick={handleApprove}
                                disabled={processing}
                            >
                                {processing ? (
                                    <>
                                        <span className="apple-spinner"></span>
                                        กำลังดำเนินการ
                                    </>
                                ) : 'อนุมัติ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Send 50 Tawi Modal - อัพโหลดไฟล์จากระบบการเงินแล้วส่งเข้าอีเมลผู้ร้องขอ */}
            {showSendModal && sendRequest && (() => {
                const recipientVerified = !!sendRequest.email_verified && !!sendRequest.user_email;
                return (
                <div className="apple-modal-overlay" onClick={() => !sending && setShowSendModal(false)}>
                    <div className="apple-modal" onClick={e => e.stopPropagation()}>
                        <div className="apple-modal-header">
                            <h2>อัพโหลด &amp; ส่ง 50 ทวิ ทางอีเมล</h2>
                            <p className="apple-modal-subtitle">แนบไฟล์ PDF 50 ทวิ ที่ออกจากระบบการเงิน (แนบได้หลายไฟล์ สูงสุด 10 ไฟล์) เพื่อส่งเข้าอีเมลผู้ร้องขอ</p>
                        </div>

                        <div className="apple-modal-body">
                            <div className="apple-info-card">
                                <div className="apple-info-row">
                                    <span className="apple-info-label">ผู้ร้องขอ</span>
                                    <span className="apple-info-value">{sendRequest.user_name}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">เลขบัตร</span>
                                    <span className="apple-info-value">{sendRequest.user_cid}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">งวด</span>
                                    <span className="apple-info-value">ปี {sendRequest.document_year + 543}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">อีเมลปลายทาง</span>
                                    <span className="apple-info-value">{sendRequest.user_email || '-'}</span>
                                </div>
                            </div>

                            {!recipientVerified ? (
                                <div className="alert alert-error" style={{ marginTop: 12 }}>
                                    ผู้ร้องขอยังไม่ได้ยืนยันอีเมล จึงยังส่งเอกสารไม่ได้ —
                                    กรุณาให้ผู้ร้องขอยืนยันอีเมลในหน้าโปรไฟล์ก่อน
                                </div>
                            ) : (
                                <>
                                    {sendRequest.emailed_at && (
                                        <div className="alert alert-info" style={{ marginTop: 12 }}>
                                            เคยส่งไปแล้วเมื่อ {new Date(sendRequest.emailed_at).toLocaleString('th-TH')} — การส่งใหม่จะแทนที่ไฟล์เดิม
                                        </div>
                                    )}
                                    <div className="apple-form-group" style={{ marginTop: 12 }}>
                                        <label className="apple-label">ไฟล์ 50 ทวิ (PDF เท่านั้น — เลือกได้หลายไฟล์)</label>
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            multiple
                                            className="apple-input"
                                            onChange={(e) => setSendFiles(Array.from(e.target.files || []))}
                                        />
                                        {sendFiles.length > 0 && (
                                            <div className="send-file-list">
                                                {sendFiles.map((f, i) => (
                                                    <div key={i} className="send-file-item">
                                                        <span className="send-file-name">📄 {f.name}</span>
                                                        <span className="send-file-size">{(f.size / 1024).toFixed(0)} KB</span>
                                                    </div>
                                                ))}
                                                <div className="send-file-total">
                                                    รวม {sendFiles.length} ไฟล์ · {(sendFiles.reduce((s, f) => s + f.size, 0) / 1024).toFixed(0)} KB
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="alert alert-warning" style={{ marginTop: 4 }}>
                                        ⚠️ ไฟล์นี้จะถูกส่งตามจริง <strong>โดยไม่เข้ารหัส</strong> เข้าอีเมลที่ยืนยันแล้วของผู้ร้องขอ
                                        — โปรดตรวจสอบว่าเป็นไฟล์ของบุคคลนี้ถูกต้องก่อนส่ง
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="apple-modal-footer">
                            <button
                                className="apple-btn apple-btn-secondary"
                                onClick={() => setShowSendModal(false)}
                                disabled={sending}
                            >
                                ยกเลิก
                            </button>
                            <button
                                className="apple-btn apple-btn-primary"
                                onClick={handleSend50Tawi}
                                disabled={sending || !recipientVerified || sendFiles.length === 0}
                            >
                                {sending ? (
                                    <>
                                        <span className="apple-spinner"></span>
                                        กำลังส่ง
                                    </>
                                ) : 'ส่งเข้าอีเมล'}
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* Sent 50 Tawi List Modal */}
            {showSentListModal && (
                <div className="apple-modal-overlay" onClick={() => setShowSentListModal(false)}>
                    <div className="apple-modal" onClick={e => e.stopPropagation()}>
                        <div className="apple-modal-header">
                            <h2>50 ทวิ ที่ส่งทางอีเมลแล้ว</h2>
                            <p className="apple-modal-subtitle">ทั้งหมด {sentCount} ราย</p>
                        </div>
                        <div className="apple-modal-body">
                            {sentList.length === 0 ? (
                                <p className="text-muted">ยังไม่มีรายการ</p>
                            ) : (
                                <div className="sent-list">
                                    {sentList.map(item => (
                                        <div key={item.id} className="sent-list-item">
                                            <div className="sent-list-main">
                                                <strong>{item.user_name}</strong>
                                                <small>{item.user_cid}</small>
                                            </div>
                                            <div className="sent-list-meta">
                                                <span>ปี {item.document_year + 543}</span>
                                                <span>📧 {item.emailed_to}</span>
                                                <span>{new Date(item.emailed_at).toLocaleString('th-TH')}</span>
                                                {item.emailed_by_name && <span>โดย {item.emailed_by_name}</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="apple-modal-footer">
                            <button className="apple-btn apple-btn-secondary" onClick={() => setShowSentListModal(false)}>
                                ปิด
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal - Apple Style */}
            {showRejectModal && selectedRequest && (
                <div className="apple-modal-overlay" onClick={() => setShowRejectModal(false)}>
                    <div className="apple-modal" onClick={e => e.stopPropagation()}>
                        <div className="apple-modal-header">
                            <h2>ปฏิเสธคำร้อง</h2>
                            <p className="apple-modal-subtitle">กรุณาระบุเหตุผลในการปฏิเสธ</p>
                        </div>

                        <div className="apple-modal-body">
                            <div className="apple-info-card">
                                <div className="apple-info-row">
                                    <span className="apple-info-label">ผู้ขอ</span>
                                    <span className="apple-info-value">{selectedRequest.user_name}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">ประเภท</span>
                                    <span className="apple-info-value">{getRequestTypeName(selectedRequest.request_type)}</span>
                                </div>
                            </div>

                            <div className="apple-form-group">
                                <label className="apple-label">เหตุผลในการปฏิเสธ <span className="required">*</span></label>
                                <textarea
                                    className="apple-textarea"
                                    rows="4"
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="กรุณาระบุเหตุผลที่ปฏิเสธคำร้องนี้..."
                                />
                            </div>
                        </div>

                        <div className="apple-modal-footer">
                            <button
                                className="apple-btn apple-btn-secondary"
                                onClick={() => setShowRejectModal(false)}
                            >
                                ยกเลิก
                            </button>
                            <button
                                className="apple-btn apple-btn-danger"
                                onClick={handleReject}
                                disabled={processing || !rejectReason.trim()}
                            >
                                {processing ? (
                                    <>
                                        <span className="apple-spinner"></span>
                                        กำลังดำเนินการ
                                    </>
                                ) : 'ปฏิเสธ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* History Modal - Apple Style */}
            {showHistoryModal && selectedRequest && (
                <div className="apple-modal-overlay" onClick={() => setShowHistoryModal(false)}>
                    <div className="apple-modal apple-modal-wide" onClick={e => e.stopPropagation()}>
                        <div className="apple-modal-header">
                            <h2>ประวัติคำร้อง</h2>
                            <p className="apple-modal-subtitle">คำร้อง #{selectedRequest.id}</p>
                        </div>

                        <div className="apple-modal-body">
                            <div className="apple-timeline">
                                {history.map((item, index) => (
                                    <div key={item.id} className={`apple-timeline-item ${item.action}`}>
                                        <div className="apple-timeline-dot"></div>
                                        <div className="apple-timeline-content">
                                            <div className="apple-timeline-header">
                                                <span className="apple-timeline-action">
                                                    {item.action === 'created' && 'สร้างคำร้อง'}
                                                    {item.action === 'approved' && 'อนุมัติ'}
                                                    {item.action === 'rejected' && 'ปฏิเสธ'}
                                                    {item.action === 'completed' && 'รับเอกสารแล้ว'}
                                                    {item.action === 'cancelled' && 'ยกเลิก'}
                                                </span>
                                                <span className="apple-timeline-time">
                                                    {new Date(item.created_at).toLocaleString('th-TH', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </div>
                                            {item.action_note && (
                                                <p className="apple-timeline-note">{item.action_note}</p>
                                            )}
                                            <span className="apple-timeline-by">โดย {item.action_by_name}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="apple-modal-footer apple-modal-footer-center">
                            <button
                                className="apple-btn apple-btn-secondary"
                                onClick={() => setShowHistoryModal(false)}
                            >
                                ปิด
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* User Profile Modal - Apple Style */}
            {showUserModal && selectedUser && (
                <div className="apple-modal-overlay" onClick={() => setShowUserModal(false)}>
                    <div className="apple-modal" onClick={e => e.stopPropagation()}>
                        <div className="apple-modal-header">
                            <h2>ข้อมูลผู้ยื่นคำร้อง</h2>
                            <p className="apple-modal-subtitle">ข้อมูลส่วนตัวที่จำเป็น</p>
                        </div>

                        <div className="apple-modal-body">
                            {/* User Avatar & Name */}
                            <div className="user-profile-header">
                                <div className="user-profile-avatar">
                                    {selectedUser.name?.charAt(0) || 'U'}
                                </div>
                                <div className="user-profile-name">
                                    <h3>{selectedUser.name}</h3>
                                    <span className="user-profile-cid">{selectedUser.cid}</span>
                                </div>
                            </div>

                            {/* User Info Card */}
                            <div className="apple-info-card">
                                <div className="apple-info-row">
                                    <span className="apple-info-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                            <circle cx="12" cy="7" r="4"/>
                                        </svg>
                                        ชื่อ-สกุล
                                    </span>
                                    <span className="apple-info-value">{selectedUser.name}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <rect x="2" y="5" width="20" height="14" rx="2"/>
                                            <line x1="2" y1="10" x2="22" y2="10"/>
                                        </svg>
                                        เลขบัตรประชาชน
                                    </span>
                                    <span className="apple-info-value">{selectedUser.cid}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                                            <polyline points="9 22 9 12 15 12 15 22"/>
                                        </svg>
                                        แผนก/หน่วยงาน
                                    </span>
                                    <span className="apple-info-value">{selectedUser.department}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                            <circle cx="8.5" cy="7" r="4"/>
                                            <polyline points="17 11 19 13 23 9"/>
                                        </svg>
                                        ตำแหน่ง
                                    </span>
                                    <span className="apple-info-value">{selectedUser.position}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                        </svg>
                                        เบอร์โทรศัพท์
                                    </span>
                                    <span className="apple-info-value">{selectedUser.phone}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                            <polyline points="22,6 12,13 2,6"/>
                                        </svg>
                                        อีเมล
                                    </span>
                                    <span className="apple-info-value">{selectedUser.email}</span>
                                </div>
                            </div>
                        </div>

                        <div className="apple-modal-footer apple-modal-footer-center">
                            <button
                                className="apple-btn apple-btn-secondary"
                                onClick={() => setShowUserModal(false)}
                            >
                                ปิด
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Print Document Modal - Apple Style */}
            {showPrintModal && printRequest && (
                <div className="apple-modal-overlay" onClick={() => setShowPrintModal(false)}>
                    <div className="apple-modal apple-modal-wide" onClick={e => e.stopPropagation()}>
                        <div className="apple-modal-header">
                            <h2>พิมพ์เอกสาร</h2>
                            <p className="apple-modal-subtitle">ตรวจสอบข้อมูลก่อนสั่งพิมพ์</p>
                        </div>

                        <div className="apple-modal-body">
                            {/* Request Info */}
                            <div className="print-request-info">
                                <div className="print-request-header">
                                    <div className="print-request-avatar">
                                        {printRequest.user_name?.charAt(0) || 'U'}
                                    </div>
                                    <div className="print-request-user">
                                        <h3>{printRequest.user_name}</h3>
                                        <span>{printRequest.user_cid}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Document Info Card */}
                            <div className="apple-info-card">
                                <div className="apple-info-row">
                                    <span className="apple-info-label">ประเภทเอกสาร</span>
                                    <span className="apple-info-value">{getRequestTypeName(printRequest.request_type)}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">งวด/ปี</span>
                                    <span className="apple-info-value">
                                        {printRequest.request_type === 'salary_slip'
                                            ? `${getMonthName(printRequest.document_month)} ${printRequest.document_year + 543}`
                                            : `ปี ${printRequest.document_year + 543}`
                                        }
                                    </span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">สถานะ</span>
                                    <span className="apple-info-value">{getStatusBadge(printRequest.status)}</span>
                                </div>
                                <div className="apple-info-row">
                                    <span className="apple-info-label">อนุญาตพิมพ์เอง</span>
                                    <span className="apple-info-value">
                                        {printRequest.allow_self_print ? (
                                            <span className="badge badge-success">ใช่</span>
                                        ) : (
                                            <span className="badge badge-muted">ไม่</span>
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Print Action Buttons */}
                            <div className="print-actions-grid">
                                {printRequest.request_type === 'salary_slip' ? (
                                    <button
                                        className="print-action-card slip"
                                        onClick={handlePrintSlip}
                                    >
                                        <div className="print-action-icon">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                                            </svg>
                                        </div>
                                        <div className="print-action-content">
                                            <h4>พิมพ์สลิปเงินเดือน</h4>
                                            <p>{getMonthName(printRequest.document_month)} {printRequest.document_year + 543}</p>
                                        </div>
                                        <div className="print-action-arrow">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="9 18 15 12 9 6"/>
                                            </svg>
                                        </div>
                                    </button>
                                ) : (
                                    <button
                                        className="print-action-card tawi"
                                        onClick={handlePrint50Tawi}
                                    >
                                        <div className="print-action-icon">
                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                                            </svg>
                                        </div>
                                        <div className="print-action-content">
                                            <h4>พิมพ์หนังสือรับรอง 50 ทวิ</h4>
                                            <p>ปีภาษี {printRequest.document_year + 543}</p>
                                        </div>
                                        <div className="print-action-arrow">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="9 18 15 12 9 6"/>
                                            </svg>
                                        </div>
                                    </button>
                                )}
                            </div>

                            <p className="print-note">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                                </svg>
                                เอกสารจะเปิดในหน้าต่างใหม่สำหรับตรวจสอบและสั่งพิมพ์
                            </p>
                        </div>

                        <div className="apple-modal-footer apple-modal-footer-center">
                            <button
                                className="apple-btn apple-btn-secondary"
                                onClick={() => setShowPrintModal(false)}
                            >
                                ปิด
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminRequests;
